/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState, useEffect } from 'react'
import log from 'loglevel'
import { GridHeader, GridRow } from 'src/components/Reusable/Grid'
import { TextInput } from 'src/components/Reusable/Inputs'
import { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown/SelectDropdown'
import { MenuPlaceholder } from 'src/components/Placeholders/MenuPlaceholder'
import {
  IconButton,
  IconButtonVariant,
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { useControls } from 'src/components/async/model/control'
import { FieldDef, SectionDef } from 'src/xgenerated/receptor'
import { ReceptorCredentialsField } from 'src/components/ModalForms/Receptor/ActivateReceptorForm/ActivateReceptorForm.components'
import { InputType } from 'src/components/ModalForms/Receptor/ActivateReceptorForm/ActivateReceptorForm.constants'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import {
  ReceptorsConfigType,
  useReceptorConfigContext,
} from '../../context/ReceptorConfigContext'
import { ReactComponent as DeleteIcon } from '../../../../components/Icons/assets/delete-icon.svg'
import { ControlMappingEntry } from './ConfigureReceptorForm.constants'
import {
  ConfigureControlMappingsButtonContainer,
  ConfigureControlMappingsCheckboxContainer,
  ConfigureControlMappingsGrid,
  ConfigureManualControlMappingsGrid,
  ConfigureProjectlMappingsGrid,
  ConfigureReceptorFormSectionContainer,
  ConfigureReceptorFormSectionTitle,
  ConfigureReceptorFormSectionDescription,
  MultiLineTextInput,
} from './ConfigureReceptorForm.styles'
import {
  createNewControlMapping,
  getControls,
  getSources,
} from './ConfigureReceptorForm.helpers'

const AddControlMappingButton = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => {
  const { addMappingFunc } = useReceptorConfigContext()
  const addMapping = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    addMappingFunc(fieldName, fieldKey)
  }

  return (
    <StandardButton
      onClick={addMapping}
      buttonSize={StandardButtonSize.MEDIUM}
      variant={StandardButtonVariant.SECONDARY}
    >
      Add Mapping
    </StandardButton>
  )
}

const ControlMappingsGridRow = ({
  mapping,
  fieldKey,
  idx,
  controls,
  items,
  removeMapping,
}: {
  mapping: ControlMappingEntry
  fieldKey: string
  idx: number
  controls: SelectItem[]
  items: SelectItem[]
  removeMapping: () => void
}): JSX.Element => {
  const onRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    removeMapping()
  }

  const { updateMappings } = useReceptorConfigContext()

  const onSelectControl = (selected: SelectItem[]) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      control: selected[0],
      controlError: '',
    })
  }

  const onSelectSource = (selected: SelectItem[]) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      source: selected[0],
      sourceError: '',
    })
  }

  return (
    <GridRow>
      <SelectDropdown
        label={''}
        defaultValues={[mapping.control]}
        items={controls}
        onSelectCb={onSelectControl}
        placeholder={'Select Control'}
        errMsg={mapping.controlError}
        alwaysShowError
      />
      <SelectDropdown
        label={''}
        defaultValues={[mapping.source]}
        items={items}
        onSelectCb={onSelectSource}
        placeholder={'Select Field'}
        errMsg={mapping.sourceError}
        alwaysShowError
      />
      <IconButton onClick={onRemove} variant={IconButtonVariant.icon}>
        <DeleteIcon />
      </IconButton>
    </GridRow>
  )
}

const ControlManualMappingsGridRow = ({
  mapping,
  fieldKey,
  idx,
  controls,
  removeMapping,
}: {
  mapping: ControlMappingEntry
  fieldKey: string
  idx: number
  controls: SelectItem[]
  removeMapping: () => void
}): JSX.Element => {
  const onRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    removeMapping()
  }

  const { updateMappings } = useReceptorConfigContext()

  const onSelectControl = (selected: SelectItem[]) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      control: selected[0],
      controlError: '',
    })
  }

  const onSourceTextChange = (sourceUrl: string) => {
    // TODO: These are confluence page specific handling.
    // Extend by passing and checking the receptor type
    const cleanUrl = sourceUrl.split('?')[0]
    const urlParts = cleanUrl.split('/')

    const index = urlParts.indexOf('spaces')
    const spaceKey = index !== -1 ? urlParts[index + 1] : null

    let pageTitle = urlParts.pop()
    pageTitle = pageTitle ? pageTitle.replace(/\+/g, ' ') : sourceUrl

    const pageName = spaceKey + ' - ' + pageTitle

    const sourceItem: SelectItem = {
      name: pageName,
      value: sourceUrl,
    }

    updateMappings(fieldKey, idx, {
      ...mapping,
      source: sourceItem,
      sourceError: '',
    })
  }

  return (
    <GridRow>
      <SelectDropdown
        label={''}
        defaultValues={[mapping.control]}
        items={controls}
        onSelectCb={onSelectControl}
        placeholder={'Select Control'}
        errMsg={mapping.controlError}
        alwaysShowError
      />
      <TextInput
        initVal={mapping.source.value.toString()}
        name={''}
        label={'manual-config-input'}
        placeholder={'URL'}
        errorMessage={mapping.sourceError}
        onChangeCb={onSourceTextChange}
        hideLabel={true}
        mb={0}
        isWarning
        allowEmptyVal={true}
      />
      <IconButton onClick={onRemove} variant={IconButtonVariant.icon}>
        <DeleteIcon />
      </IconButton>
    </GridRow>
  )
}

const ControlMappingsGridRows = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => {
  const { receptor, mappings, removeMappingFunc } = useReceptorConfigContext()
  const currentMappings = mappings[fieldKey] || [
    createNewControlMapping(fieldName, fieldKey),
  ]
  const config = receptor?.config
  const { data, isLoading, error } = useControls()
  if (isLoading) {
    return <MenuPlaceholder />
  } else if (!data) {
    if (error) {
      log.error('Error fetching controls in configure receptor modal', error)
    }
    return <></>
  }
  const controls = data.getItemsList().map((control) => ({
    name: `${control.getModelId()} ${control.getName()}`,
    value: control.getId(),
  }))
  const sources = config ? getSources(config, fieldKey) : []

  return (
    <>
      {currentMappings.map((mapping, index) => (
        <ControlMappingsGridRow
          key={`control-mapping-${index}`}
          fieldKey={fieldKey}
          idx={index}
          mapping={mapping}
          controls={controls}
          items={sources}
          removeMapping={() =>
            removeMappingFunc(fieldName, fieldKey, index, false)
          }
        />
      ))}
      <></>
    </>
  )
}

const ControlManualMappingsGridRows = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => {
  const { mappings, removeMappingFunc } = useReceptorConfigContext()
  const currentMappings = mappings[fieldKey] || [
    createNewControlMapping(fieldName, fieldKey),
  ]
  const { data, isLoading, error } = useControls()
  if (isLoading) {
    return <MenuPlaceholder />
  } else if (!data) {
    if (error) {
      log.error('Error fetching controls in configure receptor modal', error)
    }
    return <></>
  }
  const controls = data.getItemsList().map((control) => ({
    name: `${control.getModelId()} ${control.getName()}`,
    value: control.getId(),
  }))

  return (
    <>
      {currentMappings.map((mapping, index) => (
        <ControlManualMappingsGridRow
          key={`control-mapping-${index}`}
          fieldKey={fieldKey}
          idx={index}
          mapping={mapping}
          controls={controls}
          removeMapping={() =>
            removeMappingFunc(fieldName, fieldKey, index, true)
          }
        />
      ))}
      <></>
    </>
  )
}

const ControlMappingsGridHeader = ({
  fieldName,
}: {
  fieldName: string
}): JSX.Element => {
  return (
    <GridRow>
      <GridHeader>Control</GridHeader>
      <GridHeader>{fieldName}</GridHeader>
      <GridHeader></GridHeader>
    </GridRow>
  )
}

export const ConfigureControlMappings = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => (
  <>
    <ConfigureControlMappingsGrid
      gridTemplateColumns={'minmax(0, 1fr) minmax(0, 1fr) max-content'}
    >
      <ControlMappingsGridHeader fieldName={fieldName} />
      <ControlMappingsGridRows fieldName={fieldName} fieldKey={fieldKey} />
    </ConfigureControlMappingsGrid>
    <ConfigureControlMappingsButtonContainer>
      <AddControlMappingButton fieldName={fieldName} fieldKey={fieldKey} />
    </ConfigureControlMappingsButtonContainer>
  </>
)

export const ConfigureManualControlMappings = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => (
  <>
    <ConfigureManualControlMappingsGrid
      gridTemplateColumns={'minmax(0, 1fr) minmax(0, 1fr) max-content'}
    >
      <ControlMappingsGridHeader fieldName={fieldName} />
      <ControlManualMappingsGridRows
        fieldName={fieldName}
        fieldKey={fieldKey}
      />
    </ConfigureManualControlMappingsGrid>
    <ConfigureControlMappingsButtonContainer>
      <AddControlMappingButton fieldName={fieldName} fieldKey={fieldKey} />
    </ConfigureControlMappingsButtonContainer>
  </>
)

const StackedMappingsGridHeader = ({
  fieldName,
}: {
  fieldName: string
}): JSX.Element => {
  return (
    <GridRow>
      <GridHeader>Control</GridHeader>
      <GridHeader>{fieldName}</GridHeader>
      <GridHeader>Split Evidence</GridHeader>
      <GridHeader></GridHeader>
    </GridRow>
  )
}

const StackedMappingsGridRows = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => {
  const { receptor, mappings, removeMappingFunc } = useReceptorConfigContext()
  const currentMappings = mappings[fieldKey] || [
    createNewControlMapping(fieldName, fieldKey),
  ]
  const config = receptor?.config
  const { data, isLoading, error } = useControls()
  if (isLoading) {
    return <MenuPlaceholder />
  } else if (!data) {
    if (error) {
      log.error('Error fetching controls in configure receptor modal', error)
    }
    return <></>
  }
  const controls = data.getItemsList().map((control) => ({
    name: `${control.getModelId()} ${control.getName()}`,
    value: control.getId(),
  }))
  // TODO: make this dynamic for other services that would use stacked evidences
  const sources = config ? getSources(config, 'labels') : []

  return (
    <>
      {currentMappings.map((mapping, index) => (
        <StackedMappingsGridRow
          key={`control-mapping-${index}`}
          fieldKey={fieldKey}
          idx={index}
          mapping={mapping}
          controls={controls}
          items={sources}
          removeMapping={() =>
            removeMappingFunc(fieldName, fieldKey, index, false)
          }
        />
      ))}
      <></>
    </>
  )
}

const StackedMappingsGridRow = ({
  mapping,
  fieldKey,
  idx,
  controls,
  items,
  removeMapping,
}: {
  mapping: ControlMappingEntry & { isSplit?: boolean }
  fieldKey: string
  idx: number
  controls: SelectItem[]
  items: SelectItem[]
  removeMapping: () => void
}): JSX.Element => {
  const onRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    removeMapping()
  }

  const { updateMappings } = useReceptorConfigContext()

  const onSelectControl = (selected: SelectItem[]) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      control: selected[0],
      controlError: '',
    })
  }

  const onSelectSource = (selected: SelectItem[]) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      source: selected[0],
      sourceError: '',
    })
  }

  const onToggleSplit = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      isSplit: e.currentTarget.checked,
    })
  }

  return (
    <GridRow>
      <SelectDropdown
        label={''}
        defaultValues={[mapping.control]}
        items={controls}
        onSelectCb={onSelectControl}
        placeholder={'Select Control'}
        errMsg={mapping.controlError}
        alwaysShowError
      />
      <SelectDropdown
        label={''}
        defaultValues={[mapping.source]}
        items={items}
        onSelectCb={onSelectSource}
        placeholder={'Select Field'}
        errMsg={mapping.sourceError}
        alwaysShowError
      />
      <ConfigureControlMappingsCheckboxContainer>
        <CheckBoxInput
          checked={!!mapping.isSplit}
          onChange={onToggleSplit}
          aria-label="Toggle split for this mapping"
        />
      </ConfigureControlMappingsCheckboxContainer>
      <IconButton onClick={onRemove} variant={IconButtonVariant.icon}>
        <DeleteIcon />
      </IconButton>
    </GridRow>
  )
}

export const ConfigureStackedMappings = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => (
  <>
    <ConfigureControlMappingsGrid
      gridTemplateColumns={
        'minmax(0, 44%) minmax(0, 44%) minmax(0, 6%) max-content'
      }
    >
      <StackedMappingsGridHeader fieldName={fieldName} />
      <StackedMappingsGridRows fieldName={fieldName} fieldKey={fieldKey} />
    </ConfigureControlMappingsGrid>
    <ConfigureControlMappingsButtonContainer>
      <AddControlMappingButton fieldName={fieldName} fieldKey={fieldKey} />
    </ConfigureControlMappingsButtonContainer>
  </>
)

export const ConfigureManualControlMappingsTextArea = ({
  fieldName,
  fieldKey,
  label = 'Enter items (one per line)',
}: {
  fieldName: string
  fieldKey: string
  label?: string
}): JSX.Element => {
  const { config, setConfig } = useReceptorConfigContext()

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value
    const updatedConfig = { ...config }
    updatedConfig.fetch_sources = updatedConfig.fetch_sources || {}
    updatedConfig.fetch_sources[fieldKey] = value
      .split('\n')
      .map((item) => ({ id: item, display: item }))
    setConfig(updatedConfig)
  }

  const initValue =
    config.fetch_sources?.[fieldKey]?.map((item) => item.display).join('\n') ||
    ''

  return (
    <div>
      <ConfigureReceptorFormSectionTitle>
        {label}
      </ConfigureReceptorFormSectionTitle>
      <MultiLineTextInput
        name={`${fieldName}-${fieldKey}`}
        as="textarea"
        value={initValue}
        placeholder="Type each item on a new line"
        onChange={handleTextChange}
      />
    </div>
  )
}

export const ConfigureAdvancedMappingsJson = ({
  fieldName,
  fieldKey,
  label,
}: {
  fieldName: string
  fieldKey: string
  label?: string
}): JSX.Element => {
  const ADVANCED_MAPPINGS_PLACEHOLDER = `{
    "caption_modifier": 0 // 0 = off N = number of characters from the ticket title to append to the evidence caption to better distinguish attachments (recommended: 150)
  }`

  const { config, setConfig } = useReceptorConfigContext()

  const [text, setText] = useState<string>('')

  // Initialize textarea with pretty JSON from existing config (strict array shape)
  useEffect(() => {
    const current =
      (config?.fetch_sources?.[fieldKey] as
        | { id: string; display: string }[]
        | undefined) || []

    const display = current[0]?.display || ''
    if (!display) {
      setText('')
      return
    }

    try {
      const parsed = JSON.parse(display)
      setText(JSON.stringify(parsed, null, 2))
    } catch {
      // If saved value isn't valid JSON, just show it raw
      setText(display)
    }
  }, [config, fieldKey])

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value
    setText(value)
  }

  const handleBlur = () => {
    const value = text

    if (!value.trim()) {
      const updatedConfig = { ...(config || {}) }
      updatedConfig.fetch_sources = { ...(updatedConfig.fetch_sources || {}) }

      if (updatedConfig.fetch_sources) {
        delete updatedConfig.fetch_sources[fieldKey]
      }

      setConfig(updatedConfig)
      return
    }

    try {
      const parsed = JSON.parse(value)
      const minified = JSON.stringify(parsed)
      const pretty = JSON.stringify(parsed, null, 2)

      const updatedConfig = { ...(config || {}) }
      updatedConfig.fetch_sources = { ...(updatedConfig.fetch_sources || {}) }

      updatedConfig.fetch_sources[fieldKey] = [
        { id: 'advance_mappings', display: minified },
      ]

      setConfig(updatedConfig)
      setText(pretty)
    } catch {
      // keep current text, do not update config until valid JSON
    }
  }

  return (
    <div>
      <ConfigureReceptorFormSectionTitle>
        {label}
      </ConfigureReceptorFormSectionTitle>
      <MultiLineTextInput
        name={`${fieldName}-${fieldKey}`}
        as="textarea"
        value={text}
        placeholder={ADVANCED_MAPPINGS_PLACEHOLDER}
        onChange={handleTextChange}
        onBlur={handleBlur}
      />
    </div>
  )
}

export const ConfigureListConfig = ({
  fieldKey,
}: {
  fieldKey: string
}): JSX.Element => {
  const { receptor, config, setConfig } = useReceptorConfigContext()
  const sources = useMemo(() => {
    return receptor?.config
      ? (JSON.parse(receptor.config) as ReceptorsConfigType)['all_sources'][
          fieldKey
        ]
      : []
  }, [receptor?.config, fieldKey])

  const isSelected = useMemo(() => {
    return (id: string): boolean => {
      if (!config.fetch_sources || !config.fetch_sources.control_sources) {
        return false
      }
      return config.fetch_sources.control_sources.some((source) => {
        return source.id === id
      })
    }
  }, [config])
  const toggleItem = (value: Record<string, string>) => {
    const newConfig = { ...config }
    if (!newConfig.fetch_sources) {
      newConfig.fetch_sources = { control_sources: [] }
    }
    const controlSources = newConfig.fetch_sources.control_sources || []
    const exists = controlSources.some((source) => source.id === value.id)

    if (exists) {
      newConfig.fetch_sources.control_sources = controlSources.filter(
        (source) => source.id !== value.id,
      )
    } else {
      newConfig.fetch_sources.control_sources = [...controlSources, value]
    }

    setConfig(newConfig)
  }
  const sortedSources = useMemo(() => {
    if (!sources || sources.length === 0) return sources
    if (sources[0]?.display !== undefined) {
      return [...sources].sort((a, b) => a.display.localeCompare(b.display))
    }
    return sources
  }, [sources])

  return (
    <>
      {sortedSources.map((value) => (
        <FlexRow
          key={value.id}
          align={FlexAlign.FLEX_START}
          justify={FlexAlign.FLEX_START}
          gap={16}
        >
          <CheckBoxInput
            checked={isSelected(value.id)}
            onClick={() => toggleItem(value)}
            readOnly
          />
          <div key={value.id}>{value.display}</div>
        </FlexRow>
      ))}
    </>
  )
}

export const ConfigureReceptorFormSection = ({
  section,
  hideTitle,
}: {
  section: SectionDef
  hideTitle: boolean
}): JSX.Element => {
  const { title, description, fields } = section
  const { receptor } = useReceptorConfigContext()

  if (!receptor) {
    return <></>
  }

  return (
    <ConfigureReceptorFormSectionContainer>
      {!hideTitle && (
        <ConfigureReceptorFormSectionTitle>
          {title}
        </ConfigureReceptorFormSectionTitle>
      )}
      <ConfigureReceptorFormSectionDescription>
        {description}
      </ConfigureReceptorFormSectionDescription>
      <p></p>
      {fields.map((field: FieldDef) => {
        const type = (field.input_type as InputType) || InputType.TEXT
        const fieldName = field.display
        const fieldKey = field.field

        switch (type) {
          case InputType.SELECT:
          case InputType.TEXT:
            return (
              <ConfigureManualControlMappingsTextArea
                fieldName={fieldName}
                fieldKey={fieldKey}
                label={field.display}
              />
            )
          case InputType.JSON:
            // Place holder incase we need to reuse json style advance config
            return (
              <ConfigureAdvancedMappingsJson
                fieldName={fieldName}
                fieldKey={fieldKey}
                label={field.display}
              />
            )
          case InputType.PASSWORD:
          case InputType.CONTROL_MAPPING:
            return (
              <ConfigureControlMappings
                fieldName={fieldName}
                fieldKey={fieldKey}
              />
            )
          case InputType.STACKED_MAPPING:
            return (
              <ConfigureStackedMappings
                fieldName={fieldName}
                fieldKey={fieldKey}
              />
            )
          case InputType.PROJECT_MAPPING:
            return (
              <ConfigureProjectMappings
                fieldName={fieldName}
                fieldKey={fieldKey}
              />
            )
          case InputType.ADVANCE_MAPPING:
            return (
              <ConfigureAdvanceMappings
                fieldName={fieldName}
                fieldKey={fieldKey}
              />
            )
          case InputType.MANUAL_CONTROL_MAPPING:
            return (
              <ConfigureManualControlMappings
                fieldName={fieldName}
                fieldKey={fieldKey}
              />
            )
          case InputType.LIST_CONFIG: {
            return <ConfigureListConfig fieldKey={fieldKey} />
          }
          default: {
            const fieldKey = field.field
            const sources = receptor.config
              ? (JSON.parse(receptor.config) as ReceptorsConfigType)[
                  'all_sources'
                ][fieldKey]
              : []
            const placeholder = field.placeholder
            return (
              <>
                {sources.map((value) => (
                  <ReceptorCredentialsField
                    key={field.field}
                    receptor={receptor}
                    type={type}
                    label={field.display}
                    name={field.field}
                    value={value.id}
                    placeholder={placeholder}
                    onChange={() => null}
                    errorMsg=""
                  />
                ))}
              </>
            )
          }
        }
      })}
    </ConfigureReceptorFormSectionContainer>
  )
}

export const ProjectMappingsGridRow = ({
  mapping,
  fieldKey,
  idx,
  projects,
  taskTypes,
  removeMapping,
}: {
  mapping: ControlMappingEntry
  fieldKey: string
  idx: number
  projects: SelectItem[]
  taskTypes: SelectItem[]
  removeMapping: () => void
}): JSX.Element => {
  const onRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    removeMapping()
  }

  const { updateMappings } = useReceptorConfigContext()

  const [filteredTaskTypes, setFilteredTaskTypes] = useState<SelectItem[]>(
    () => {
      const ids = mapping?.control?.childIds as string[] | undefined
      return Array.isArray(ids)
        ? taskTypes.filter((t) => ids.includes(String(t.value)))
        : []
    },
  )

  const onSelectProject = (selected: SelectItem[]) => {
    const proj = selected[0]
    const ids = proj?.childIds as string[] | undefined
    const next = Array.isArray(ids)
      ? taskTypes.filter((t) => ids.includes(String(t.value)))
      : []
    setFilteredTaskTypes(next)

    const emptyItem: SelectItem = {
      name: 'Select Task Type',
      value: '',
    }

    updateMappings(fieldKey, idx, {
      ...mapping,
      control: proj,
      controlError: '',
      source: emptyItem,
      sourceError: '',
    })
  }

  const onSelectType = (selected: SelectItem[]) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      source: selected[0],
      sourceError: '',
    })
  }

  const onNameTextChange = (val: string) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      name: val,
    })
  }

  return (
    <GridRow $overrideColumnColor>
      <div className="grid-column">
        <TextInput
          initVal={mapping.name ?? ''}
          name={'project-mapping-name'}
          label={''}
          placeholder={'Name'}
          errorMessage={mapping.nameError}
          onChangeCb={onNameTextChange}
          mb={0}
          hideLabel={true}
          isWarning
          hasError={mapping.nameError != ''}
        />
      </div>

      <div className="grid-column">
        <SelectDropdown
          label={''}
          defaultValues={[mapping.control]}
          items={projects}
          onSelectCb={onSelectProject}
          placeholder={'Select Project'}
          errMsg={mapping.controlError}
          alwaysShowError
        />
      </div>

      <div className="grid-column">
        <SelectDropdown
          key={`task-type`}
          label={''}
          defaultValues={[mapping.source]}
          items={filteredTaskTypes}
          onSelectCb={onSelectType}
          placeholder={'Select Task Type'}
          errMsg={mapping.sourceError}
          alwaysShowError
        />
      </div>

      <div className="grid-column">
        <IconButton onClick={onRemove} variant={IconButtonVariant.icon}>
          <DeleteIcon />
        </IconButton>
      </div>
    </GridRow>
  )
}

export const ProjectMappingsGridRows = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => {
  const { receptor, mappings, removeMappingFunc } = useReceptorConfigContext()
  const currentMappings = mappings[fieldKey] || [
    createNewControlMapping(fieldName, fieldKey),
  ]
  const config = receptor?.config

  const projects = config ? getControls(config, 'projects') : []

  const taskTypes = config ? getSources(config, 'task_types') : []

  return (
    <>
      {currentMappings.map((mapping, index) => (
        <ProjectMappingsGridRow
          key={`project-mapping-${index}`}
          fieldKey={fieldKey}
          idx={index}
          mapping={mapping}
          projects={projects}
          taskTypes={taskTypes}
          removeMapping={() =>
            removeMappingFunc(fieldName, fieldKey, index, true)
          }
        />
      ))}
      <></>
    </>
  )
}

export const ConfigureProjectMappings = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => {
  const { addMappingFunc } = useReceptorConfigContext()
  const addMapping = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    addMappingFunc(fieldName, fieldKey)
  }

  return (
    <>
      <ConfigureProjectlMappingsGrid
        gridTemplateColumns={
          'minmax(0, 30%) minmax(0, 30%) minmax(0, 30%) max-content'
        }
      >
        <ProjectMappingsGridRows fieldName={fieldName} fieldKey={fieldKey} />
      </ConfigureProjectlMappingsGrid>
      <ConfigureControlMappingsButtonContainer>
        <StandardButton
          onClick={addMapping}
          buttonSize={StandardButtonSize.MEDIUM}
          variant={StandardButtonVariant.SECONDARY}
        >
          Add Mapping
        </StandardButton>
      </ConfigureControlMappingsButtonContainer>
    </>
  )
}

export const AdvanceMappingsGridRow = ({
  mapping,
  fieldKey,
  idx,
  taskTypes, // tables
  subject_fields,
  enum_fields,
  enum_choices,
  removeMapping,
}: {
  mapping: ControlMappingEntry
  fieldKey: string
  idx: number
  taskTypes: SelectItem[]
  subject_fields: SelectItem[]
  enum_fields: SelectItem[]
  enum_choices: SelectItem[]
  removeMapping: () => void
}): JSX.Element => {
  const onRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    removeMapping()
  }

  const { updateMappings } = useReceptorConfigContext()

  // Filtered lists driven by parent selections
  const [filteredSubjectFields, setFilteredSubjectFields] = useState<
    SelectItem[]
  >(() => {
    const tableId =
      mapping?.source?.value != null ? String(mapping.source.value) : ''
    return tableId ? subject_fields.filter((s) => s.parentId === tableId) : []
  })
  const [filteredEnumFields, setFilteredEnumFields] = useState<SelectItem[]>(
    () => {
      const tableId =
        mapping?.source?.value != null ? String(mapping.source.value) : ''
      return tableId ? enum_fields.filter((e) => e.parentId === tableId) : []
    },
  )
  const [filteredEnumChoices, setFilteredEnumChoices] = useState<SelectItem[]>(
    () => {
      const efId =
        mapping?.enumField?.value != null ? String(mapping.enumField.value) : ''
      return efId ? enum_choices.filter((c) => c.parentId === efId) : []
    },
  )

  // --- Select handlers ---
  // 1) Table (task_type) -> resets subject & enum fields & choices
  const onSelectTable = (selected: SelectItem[]) => {
    const table = selected[0]
    const tableId = table?.value != null ? String(table.value) : ''

    const nextSubject = tableId
      ? subject_fields.filter((s) => s.parentId === tableId)
      : []
    const nextEnumFields = tableId
      ? enum_fields.filter((e) => e.parentId === tableId)
      : []

    setFilteredSubjectFields(nextSubject)
    setFilteredEnumFields(nextEnumFields)
    setFilteredEnumChoices([])

    const emptySel: SelectItem = { name: '', value: '' }

    updateMappings(fieldKey, idx, {
      ...mapping,
      source: table,
      sourceError: '',
      subjectField: emptySel,
      subjectFieldError: '',
      enumField: emptySel,
      enumFieldError: '',
      enumChoice: emptySel,
      enumChoiceError: '',
    })
  }

  // 2) Subject field (depends on table)
  const onSelectSubject = (selected: SelectItem[]) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      subjectField: selected[0],
      subjectFieldError: '',
    })
  }

  // 3) Enum field (depends on table) -> resets enum choice
  const onSelectEnumField = (selected: SelectItem[]) => {
    const ef = selected[0]
    const efId = ef?.value != null ? String(ef.value) : ''
    const nextChoices = efId
      ? enum_choices.filter((c) => c.parentId === efId)
      : []
    setFilteredEnumChoices(nextChoices)

    const emptySel: SelectItem = { name: '', value: '' }

    updateMappings(fieldKey, idx, {
      ...mapping,
      enumField: ef,
      enumFieldError: '',
      enumChoice: emptySel,
      enumChoiceError: '',
    })
  }

  // 4) Enum choice (depends on enum field)
  const onSelectEnumChoice = (selected: SelectItem[]) => {
    updateMappings(fieldKey, idx, {
      ...mapping,
      enumChoice: selected[0],
      enumChoiceError: '',
    })
  }

  const asDefault = (opt?: SelectItem): SelectItem[] => (opt ? [opt] : [])

  return (
    <GridRow $overrideColumnColor>
      {/* Tables (task types) */}
      <div className="grid-column">
        <SelectDropdown
          label={''}
          defaultValues={[mapping.source]}
          items={taskTypes}
          onSelectCb={onSelectTable}
          placeholder={'Select Table'}
          errMsg={mapping.sourceError}
          alwaysShowError
        />
      </div>

      {/* Subject fields (filtered by table) */}
      <div className="grid-column">
        <SelectDropdown
          key={`subject-field-${idx}`}
          label={''}
          defaultValues={asDefault(mapping.subjectField)}
          items={filteredSubjectFields}
          onSelectCb={onSelectSubject}
          placeholder={'Select Title Field'}
          errMsg={mapping.subjectFieldError}
          alwaysShowError
        />
      </div>

      {/* Enum fields (filtered by table) */}
      <div className="grid-column">
        <SelectDropdown
          key={`enum-field-${idx}`}
          label={''}
          defaultValues={asDefault(mapping.enumField)}
          items={filteredEnumFields}
          onSelectCb={onSelectEnumField}
          placeholder={'Select State Field'}
          errMsg={mapping.enumFieldError}
          alwaysShowError
        />
      </div>

      {/* Enum choices (filtered by enum field) */}
      <div className="grid-column">
        <SelectDropdown
          key={`enum-choice-${idx}`}
          label={''}
          defaultValues={asDefault(mapping.enumChoice)}
          items={filteredEnumChoices}
          onSelectCb={onSelectEnumChoice}
          placeholder={'Select State Choice'}
          errMsg={mapping.enumChoiceError}
          alwaysShowError
        />
      </div>

      <div className="grid-column">
        <IconButton onClick={onRemove} variant={IconButtonVariant.icon}>
          <DeleteIcon />
        </IconButton>
      </div>
    </GridRow>
  )
}

export const AdvanceMappingsGridRows = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => {
  const { receptor, mappings, removeMappingFunc } = useReceptorConfigContext()
  const currentMappings = mappings[fieldKey] || [
    createNewControlMapping(fieldName, fieldKey),
  ]
  const config = receptor?.config

  const taskTypes = config ? getSources(config, 'task_types') : []
  const subject_fields = config ? getSources(config, 'subject_fields') : []
  const enum_fields = config ? getSources(config, 'state_fields') : []
  const enum_choices = config ? getSources(config, 'state_choices') : []

  return (
    <>
      {currentMappings.map((mapping, index) => (
        <AdvanceMappingsGridRow
          key={`advance-mapping-${index}`}
          fieldKey={fieldKey}
          idx={index}
          mapping={mapping}
          taskTypes={taskTypes}
          subject_fields={subject_fields}
          enum_fields={enum_fields}
          enum_choices={enum_choices}
          removeMapping={() =>
            removeMappingFunc(fieldName, fieldKey, index, true)
          }
        />
      ))}
      <></>
    </>
  )
}

export const ConfigureAdvanceMappings = ({
  fieldName,
  fieldKey,
}: {
  fieldName: string
  fieldKey: string
}): JSX.Element => {
  const { addMappingFunc } = useReceptorConfigContext()
  const addMapping = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    addMappingFunc(fieldName, fieldKey)
  }

  return (
    <>
      <ConfigureProjectlMappingsGrid
        gridTemplateColumns={
          'minmax(0, 22%) minmax(0, 22%) minmax(0, 22%) minmax(0, 22%) max-content'
        }
      >
        <AdvanceMappingsGridRows fieldName={fieldName} fieldKey={fieldKey} />
      </ConfigureProjectlMappingsGrid>
      <ConfigureControlMappingsButtonContainer>
        <StandardButton
          onClick={addMapping}
          buttonSize={StandardButtonSize.MEDIUM}
          variant={StandardButtonVariant.SECONDARY}
        >
          Add Mapping
        </StandardButton>
      </ConfigureControlMappingsButtonContainer>
    </>
  )
}
